import React from 'react';

import {useNodeOrCategorySuggestions} from './use-node-or-category-suggestions';
import {GlobalVersionData} from '../../../../../hooks/global-version/use-global-version-query';
import {SearchHistoryRecordDto} from '../../../explore-view/hooks/requests/use-explore-view-search-history-query';
import {DocNameSuggestion, HistoryRecordSuggestion, PrefixSuggestion} from '../interface';
import {useNodeTypesQuery, useNodeTypesQueryCache} from './use-node-types-query';
import {InputSeparationVariant, parseInput} from '../parse-input';
import {useMetadataSuggestions} from './use-metadata-suggestions';
import {
    getUniqueMetadataLabels,
    isInputValueNotEmpty,
    isMetadataPrefix,
    isNodeOrCategorySearchPrefix
} from '../suggestion-helpers';

export const DOC_NAME_SEARCH_PREFIX = 'document';
export const CATEGORY_SEARCH_PREFIX = 'category';

interface Props {
    inputValue: string;
    inputPrefix: string;
    currentGlobalVersion?: GlobalVersionData;
    searchHistory?: SearchHistoryRecordDto[];
}

export const useSuggestionsProps = (props: Props) => {
    const {inputValue, inputPrefix, currentGlobalVersion, searchHistory} = props;
    const isEditMode = !props.currentGlobalVersion;

    const nodeTypesProps = {
        enabled: !!props.inputValue,
        globalVersionId: props.currentGlobalVersion?.id
    };

    const nodeTypesQuery = useNodeTypesQuery(nodeTypesProps);
    const nodeTypesQueryCache = useNodeTypesQueryCache(nodeTypesProps);

    const validNodeTypes = React.useMemo(
        () =>
            nodeTypesQuery.data &&
            [
                ...nodeTypesQuery.data,
                DOC_NAME_SEARCH_PREFIX,
                ...(isEditMode ? getUniqueMetadataLabels() : [])
            ].sort(),
        [nodeTypesQuery, isEditMode]
    );

    // if we have at least one ":" separator, it is possible to interpret input content in
    // different ways, ie interpret the whole input string as prefix, or interpret the part before
    // the separator as prefix and after as search string. the number of intepretations only grows
    // with each separator added into input, though realistically we'll rarely see more than two variants.
    // we should load suggestions for each valid variant (with the valid prefix type)
    const inputSeparationVariants = React.useMemo<InputSeparationVariant[]>(
        () =>
            parseInput({
                inputValue,
                inputPrefix,
                nodeTypes: validNodeTypes
            }),
        [inputValue, inputPrefix, validNodeTypes]
    );

    const nodeOrCategorySuggestions = useNodeOrCategorySuggestions({
        inputSeparationVariants: inputSeparationVariants.filter(
            (variant) =>
                isInputValueNotEmpty(variant.inputValue) &&
                (!variant.inputPrefix || isNodeOrCategorySearchPrefix(variant.inputPrefix))
        ) as InputSeparationVariant[],
        globalVersionId: currentGlobalVersion?.id
    });

    const metadataSuggestions = useMetadataSuggestions({
        inputSeparationVariants: inputSeparationVariants.filter(
            (variant) =>
                isInputValueNotEmpty(variant.inputValue) &&
                (!variant.inputPrefix || isMetadataPrefix(variant.inputPrefix))
        ),
        enabled: isEditMode
    });

    const isLoading = nodeOrCategorySuggestions.isLoading || nodeTypesQuery.isLoading;

    const historySuggestions = React.useMemo<HistoryRecordSuggestion[]>(
        () =>
            (!inputValue &&
                !inputPrefix &&
                searchHistory?.map((searchRecord) => ({...searchRecord, type: 'historyRecord'}))) ||
            [],
        [inputPrefix, inputValue, searchHistory]
    );

    const docSuggestion: DocNameSuggestion | undefined =
        inputValue && !inputPrefix ? {type: 'document'} : undefined;

    const prefixSuggestions = (() => {
        const inputValWithoutLeadingColon =
            inputValue[0] === ':' ? inputValue.slice(1) : inputValue;

        if (validNodeTypes && !inputPrefix) {
            return validNodeTypes
                .filter((nodeType) => nodeType.toLowerCase().includes(inputValWithoutLeadingColon))
                .map(
                    (nodeType) =>
                        ({
                            type: 'prefix',
                            prefix: nodeType
                        }) as PrefixSuggestion
                );
        }

        return [];
    })();

    return {
        isLoading,

        docSuggestion,
        nodeAndCategorySuggestions: nodeOrCategorySuggestions.data,
        metadataSuggestions: metadataSuggestions.data,
        prefixSuggestions,
        historySuggestions,
        invalidateNodeTypesQuery: nodeTypesQueryCache.invalidate
    };
};
